import { useEffect, useMemo, useRef, useState } from "react";
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  ReferenceLine, Brush,
} from "recharts";
import { api, ApiError } from "../api";
import { tg } from "../telegram";
import { useT } from "../i18n";
import { useTelegramBackButton } from "../hooks/useTelegramBackButton";
import type {
  AutoCatalog, AutoKpi, AutoSeries, AutoAnomalies, AutoAnomaly,
  AutoImportRow, AutoMetric, AutoSnapshot,
} from "../types";

type Sub = "overview" | "charts" | "anomalies" | "data";

// Telemetry timestamps are station wall-clock tagged UTC — slice the ISO
// string directly so the browser timezone never shifts them.
const fmtTs = (iso: string | null) => iso ? iso.slice(5, 16).replace("T", " ") : "—";
const fmtTime = (iso: string | null) => iso ? iso.slice(11, 16) : "—";
const fmtFull = (iso: string | null) => iso ? iso.slice(0, 16).replace("T", " ") : "—";
const fmtNum = (v: number | null | undefined, d = 1) =>
  v === null || v === undefined ? "—" : (Number.isInteger(v) ? String(v) : v.toFixed(d));

const dayOf = (iso: string | null | undefined) => (iso ? iso.slice(0, 10) : "");
const plusDayIso = (dayStr: string) => {
  const d = new Date(dayStr + "T00:00:00Z");
  d.setUTCDate(d.getUTCDate() + 1);
  return d.toISOString();
};
const TF_HOURS: Record<string, number> = { "7d": 168, "3d": 72, "1d": 24, "12h": 12, "6h": 6, "1h": 1 };

// Compute a precise [from,to] ISO window from the timeframe choice. Hour/day
// windows end at the data's last timestamp; "custom" uses the date inputs.
function windowFor(tf: string, cFrom: string, cTo: string, dataTo: string | null | undefined) {
  if (tf === "all") return { from: "", to: "" };
  if (tf === "custom") {
    return {
      from: cFrom ? `${cFrom}T00:00:00Z` : "",
      to: cTo ? plusDayIso(cTo) : "",
    };
  }
  if (!dataTo) return { from: "", to: "" };
  const end = new Date(dataTo);
  const start = new Date(end.getTime() - (TF_HOURS[tf] ?? 24) * 3600 * 1000);
  return { from: start.toISOString(), to: end.toISOString() };
}

// Custom chart tooltip — the recharts default loses the date header on the
// dark theme. Shows date+time clearly, then each series colour-matched.
function ChartTooltip({ active, payload }: {
  active?: boolean;
  payload?: { dataKey?: string; name?: string; value?: number; color?: string; payload?: { full?: string } }[];
}) {
  if (!active || !payload || payload.length === 0) return null;
  const full = payload[0]?.payload?.full ?? "";
  return (
    <div style={{
      background: "var(--bg)", border: "1px solid var(--border)", borderRadius: 8,
      padding: "8px 10px", fontSize: 12, boxShadow: "0 2px 10px rgba(0,0,0,0.25)",
    }}>
      <div style={{ fontWeight: 700, marginBottom: 4, color: "var(--text)" }}>{full}</div>
      {payload.map(s => (
        <div key={s.dataKey} style={{ color: s.color }}>{s.name}: <b>{fmtNum(s.value)}</b></div>
      ))}
    </div>
  );
}

export default function Automatika() {
  const { t } = useT();
  const [sub, setSub] = useState<Sub>("overview");
  const [tf, setTf] = useState("all");
  const [cFrom, setCFrom] = useState("");
  const [cTo, setCTo] = useState("");
  const [catalog, setCatalog] = useState<AutoCatalog | null>(null);
  const [error, setError] = useState<string | null>(null);

  const unit = 1;
  const win = useMemo(() => windowFor(tf, cFrom, cTo, catalog?.data_to), [tf, cFrom, cTo, catalog]);
  const qs = useMemo(() => {
    const p = new URLSearchParams({ unit: String(unit) });
    if (win.from) p.set("from", win.from);
    if (win.to) p.set("to", win.to);
    return p.toString();
  }, [win]);

  useEffect(() => {
    api.get<AutoCatalog>("/api/auto/metrics").then(setCatalog).catch(() => undefined);
  }, []);

  const subs: { k: Sub; label: string }[] = [
    { k: "overview",  label: t("auto.sub_overview") },
    { k: "charts",    label: t("auto.sub_charts") },
    { k: "anomalies", label: t("auto.sub_anomalies") },
    { k: "data",      label: t("auto.sub_data") },
  ];

  const TF_OPTIONS = ["all", "7d", "3d", "1d", "12h", "6h", "1h", "custom"];

  return (
    <div>
      <h1>{t("tabs.automatika")}</h1>

      {/* timeframe */}
      <div className="card" style={{ marginBottom: 12 }}>
        <div style={{ fontSize: 12, color: "var(--hint)", marginBottom: 4 }}>{t("auto.timeframe")}</div>
        <select value={tf} onChange={e => setTf(e.target.value)} style={{ width: "100%" }}>
          {TF_OPTIONS.map(o => <option key={o} value={o}>{t("auto.tf_" + o)}</option>)}
        </select>
        {tf === "custom" && (
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginTop: 8 }}>
            <label>
              <div style={{ fontSize: 12, color: "var(--hint)" }}>{t("records.from")}</div>
              <input type="date" value={cFrom} onChange={e => setCFrom(e.target.value)} />
            </label>
            <label>
              <div style={{ fontSize: 12, color: "var(--hint)" }}>{t("records.to")}</div>
              <input type="date" value={cTo} onChange={e => setCTo(e.target.value)} />
            </label>
          </div>
        )}
        {catalog?.data_from && (
          <div style={{ fontSize: 11, color: "var(--hint)", marginTop: 6 }}>
            {t("auto.data_range")}: {fmtTs(catalog.data_from)} → {fmtTs(catalog.data_to)}
          </div>
        )}
      </div>

      <div className="period-tabs" style={{ marginBottom: 12 }}>
        {subs.map(s => (
          <button key={s.k} className={"period-tab" + (sub === s.k ? " active" : "")}
            onClick={() => { tg()?.HapticFeedback?.selectionChanged?.(); setSub(s.k); }}>
            {s.label}
          </button>
        ))}
      </div>

      {error && <div className="alert error">{error}</div>}

      {sub === "overview"  && <Overview qs={qs} catalog={catalog} onErr={setError} />}
      {sub === "charts"    && <ChartsTab win={win} catalog={catalog} onErr={setError} />}
      {sub === "anomalies" && <AnomaliesTab qs={qs} unit={unit} catalog={catalog} onErr={setError} />}
      {sub === "data"      && <DataTab unit={unit} qs={qs} onErr={setError} />}
    </div>
  );
}

// ---------------- Overview ----------------

function Overview({ qs, catalog, onErr }: {
  qs: string; catalog: AutoCatalog | null; onErr: (m: string) => void;
}) {
  const { t } = useT();
  const [kpi, setKpi] = useState<AutoKpi | null>(null);
  useEffect(() => {
    setKpi(null);
    api.get<AutoKpi>(`/api/auto/kpi?${qs}`).then(setKpi).catch((e: ApiError) => onErr(e.message));
  }, [qs]);

  if (!kpi) return <div className="empty-state"><span className="spinner" /></div>;
  const m = kpi.metrics;

  // KV = envelope of the three line voltages (R/S/T), over running minutes.
  const vMins = ["voltage_rs", "voltage_st", "voltage_rt"]
    .map(k => m[k]?.min).filter((x): x is number => x != null);
  const vMaxs = ["voltage_rs", "voltage_st", "voltage_rt"]
    .map(k => m[k]?.max).filter((x): x is number => x != null);
  const kvMin = vMins.length ? Math.min(...vMins) : null;
  const kvMax = vMaxs.length ? Math.max(...vMaxs) : null;

  const minMaxTile = (cls: string, icon: string, title: string, mn: number | null, mx: number | null, d: number) => (
    <div className={`auto-tile ${cls}`}>
      <div className="auto-tile-top"><span className="auto-tile-icon">{icon}</span><span className="auto-tile-title">{title}</span></div>
      <div className="auto-tile-mm">
        <div className="auto-tile-col"><span className="auto-tile-num">{fmtNum(mn, d)}</span><span className="auto-tile-pill">min</span></div>
        <div className="auto-tile-col"><span className="auto-tile-num">{fmtNum(mx, d)}</span><span className="auto-tile-pill">max</span></div>
      </div>
    </div>
  );

  const big = kpi.energy_kwh >= 1000;

  return (
    <>
      <div className="auto-tiles">
        {minMaxTile("kw", "⚡", "KW, kW", m.active_power?.min ?? null, m.active_power?.max ?? null, 0)}
        {minMaxTile("kv", "🔌", "KV, kV", kvMin, kvMax, 1)}
        <div className="auto-tile energy">
          <div className="auto-tile-top"><span className="auto-tile-icon">🔋</span><span className="auto-tile-title">{t("auto.kpi_energy")}</span></div>
          <div className="auto-tile-bigrow">
            <span className="auto-tile-big">{big ? (kpi.energy_kwh / 1000).toFixed(1) : fmtNum(kpi.energy_kwh, 0)}</span>
            <span className="auto-tile-unit">{big ? t("auto.mwh") : t("auto.kwh")}</span>
          </div>
        </div>
      </div>

      {(catalog?.groups ?? []).map(g => {
        const ms = (catalog?.metrics ?? []).filter(x => x.group === g.key);
        const hasData = ms.some(x => m[x.key] && (m[x.key].avg !== null || m[x.key].max !== null));
        if (!hasData) return null;
        return (
          <details key={g.key} className="cat-section" open>
            <summary className="cat-summary">
              <span className="cat-summary-arrow">▾</span>
              <span className="cat-summary-label">{g.label}</span>
            </summary>
            <div style={{ overflowX: "auto" }}>
              <table className="table" style={{ fontSize: 13 }}>
                <thead><tr>
                  <th>{t("auto.col_metric")}</th><th>min</th><th>avg</th><th>max</th><th>last</th>
                </tr></thead>
                <tbody>
                  {ms.map(x => {
                    const d = m[x.key];
                    return (
                      <tr key={x.key}>
                        <td>{x.label}{x.unit ? <span style={{ color: "var(--hint)" }}> {x.unit}</span> : null}</td>
                        <td className="num">{fmtNum(d?.min)}</td>
                        <td className="num">{fmtNum(d?.avg)}</td>
                        <td className="num">{fmtNum(d?.max)}</td>
                        <td className="num">{fmtNum(d?.last)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </details>
        );
      })}
    </>
  );
}

// ---------------- Charts ----------------

const RES_OPTIONS = ["auto", "60", "30", "15", "5", "1", "raw"];

// Red dot on points whose interval contained a deviation (flag from backend).
function renderFlagDot(props: { cx?: number; cy?: number; index?: number; payload?: { flag?: boolean } }) {
  const { cx, cy, index, payload } = props;
  if (payload?.flag && cx != null && cy != null) {
    return <circle key={index} cx={cx} cy={cy} r={4} fill="#ff453a" stroke="#fff" strokeWidth={1} />;
  }
  return <circle key={index} cx={cx ?? 0} cy={cy ?? 0} r={0} fill="none" />;
}

function ChartsTab({ win, catalog, onErr }: {
  win: { from: string; to: string }; catalog: AutoCatalog | null; onErr: (m: string) => void;
}) {
  const { t } = useT();
  const [metric, setMetric] = useState("active_power");
  const [res, setRes] = useState("auto");
  // Drill-down is a stack of [from,to] windows, so the user can dive level by
  // level and step back out one at a time. Empty = the top-level timeframe.
  const [stack, setStack] = useState<{ from: string; to: string }[]>([]);
  const [brush, setBrush] = useState<{ startIndex?: number; endIndex?: number } | null>(null);
  const [series, setSeries] = useState<AutoSeries | null>(null);

  // Changing the top timeframe exits any drill-down.
  useEffect(() => { setStack([]); setBrush(null); }, [win.from, win.to]);

  const eff = stack.length ? stack[stack.length - 1] : win;
  useEffect(() => {
    setSeries(null); setBrush(null);
    const p = new URLSearchParams({ unit: "1" });
    if (eff.from) p.set("from", eff.from);
    if (eff.to) p.set("to", eff.to);
    p.set("metric", metric); p.set("bucket", res); p.set("points", "700");
    api.get<AutoSeries>(`/api/auto/series?${p.toString()}`).then(setSeries).catch((e: ApiError) => onErr(e.message));
  }, [eff.from, eff.to, metric, res]);

  const data = useMemo(() => (series?.points ?? []).map(pt => ({
    label: fmtTs(pt.t), full: fmtFull(pt.t), t: pt.t,
    min: pt.min, avg: pt.avg, max: pt.max, flag: pt.flag,
  })), [series]);

  const flaggedCount = useMemo(() => data.filter(d => d.flag).length, [data]);

  // Vertical date dividers wherever the calendar day changes — so the
  // compressed (operating-only) timeline still shows every date clearly.
  const dayMarks = useMemo(() => {
    const out: { label: string; day: string }[] = [];
    let prev = "";
    for (const d of data) {
      const day = d.t ? d.t.slice(0, 10) : "";
      if (day && day !== prev) { out.push({ label: d.label, day: day.slice(5) }); prev = day; }
    }
    return out;
  }, [data]);

  const pushDrill = (from: string, to: string) => {
    setStack(s => [...s, { from, to }]);
    setRes("auto"); setBrush(null);
    tg()?.HapticFeedback?.impactOccurred("medium");
  };
  // Tap any point → zoom to a window centred on it, a few buckets wide.
  const drillPoint = (pt: { t?: string }) => {
    if (!series || !pt?.t) return;
    const half = Math.max((series.bucket_min || 30) * 5, 10) * 60 * 1000;
    const c = new Date(pt.t).getTime();
    pushDrill(new Date(c - half).toISOString(), new Date(c + half).toISOString());
  };
  // Brush range → zoom to exactly the dragged span.
  const zoomToBrush = () => {
    if (!brush || brush.startIndex == null || brush.endIndex == null) return;
    const a = data[brush.startIndex]?.t, b = data[brush.endIndex]?.t;
    if (!a || !b || a === b) return;
    const to = new Date(new Date(b).getTime() + (series?.bucket_min || 1) * 60 * 1000).toISOString();
    pushDrill(a, to);
  };
  const canZoomBrush = !!brush && brush.startIndex != null && brush.endIndex != null
    && brush.endIndex - brush.startIndex >= 1
    && (brush.startIndex !== 0 || brush.endIndex !== data.length - 1);

  const resLabel = (r: string) =>
    r === "auto" ? t("auto.res_auto") : r === "raw" ? t("auto.res_raw") : `${r} ${t("auto.min_short")}`;

  return (
    <>
      <div className="card" style={{ marginBottom: 12 }}>
        <select value={metric} onChange={e => setMetric(e.target.value)} style={{ width: "100%" }}>
          {(catalog?.groups ?? []).map(g => (
            <optgroup key={g.key} label={g.label}>
              {(catalog?.metrics ?? []).filter(x => x.group === g.key).map((x: AutoMetric) => (
                <option key={x.key} value={x.key}>{x.label}{x.unit ? ` (${x.unit})` : ""}</option>
              ))}
            </optgroup>
          ))}
        </select>
        <div style={{ display: "flex", gap: 8, marginTop: 8, alignItems: "center", flexWrap: "wrap" }}>
          <span style={{ fontSize: 12, color: "var(--hint)" }}>{t("auto.resolution")}:</span>
          <select value={res} onChange={e => setRes(e.target.value)} style={{ flex: 1, minWidth: 110 }}>
            {RES_OPTIONS.map(r => <option key={r} value={r}>{resLabel(r)}</option>)}
          </select>
          {stack.length > 0 && (
            <>
              <button className="btn secondary" style={{ padding: "6px 10px" }}
                onClick={() => { setStack(s => s.slice(0, -1)); setBrush(null); tg()?.HapticFeedback?.impactOccurred("light"); }}>
                {t("auto.back")}
              </button>
              <button className="btn secondary" style={{ padding: "6px 10px" }}
                onClick={() => { setStack([]); setRes("auto"); setBrush(null); tg()?.HapticFeedback?.impactOccurred("light"); }}>
                {t("auto.reset_zoom")}
              </button>
            </>
          )}
        </div>
      </div>

      {!series && <div className="empty-state"><span className="spinner" /></div>}
      {series && data.length === 0 && <div className="empty-state">{t("common.no_data")}</div>}
      {series && data.length > 0 && (
        <div className="card">
          <div style={{ fontSize: 13, color: "var(--hint)", marginBottom: 6 }}>
            {series.label}{series.unit ? `, ${series.unit}` : ""} ·{" "}
            {series.resolution === "raw" ? t("auto.res_raw")
              : `${t("auto.bucket")} ${series.bucket_min} ${t("auto.min_short")}`}
            {stack.length > 0 && (
              <span> · 🔎 {fmtTs(data[0]?.t)} → {fmtTime(data[data.length - 1]?.t)}</span>
            )}
            {flaggedCount > 0 && (
              <span style={{ color: "#ff453a" }}> · 🔴 {t("auto.deviations_n", { n: flaggedCount })}</span>
            )}
          </div>
          <ResponsiveContainer width="100%" height={360}>
            <LineChart data={data} margin={{ top: 14, right: 10, left: 0, bottom: 4 }}
              onClick={(st: { activePayload?: { payload?: { t?: string } }[] }) => {
                const p = st?.activePayload?.[0]?.payload;
                if (p) drillPoint(p);
              }}>
              <CartesianGrid strokeDasharray="3 3" opacity={0.25} />
              <XAxis dataKey="label" tick={{ fontSize: 9 }} minTickGap={28} />
              <YAxis tick={{ fontSize: 10 }} width={44} />
              <Tooltip content={<ChartTooltip />} />
              {dayMarks.map(dm => (
                <ReferenceLine key={dm.label} x={dm.label} stroke="var(--hint)" strokeDasharray="2 4" opacity={0.55}
                  label={{ value: dm.day, position: "insideTop", fontSize: 9, fill: "var(--hint)" }} />
              ))}
              <Line type="monotone" dataKey="max" name="max" stroke="#ff9500" strokeWidth={1}
                dot={false} isAnimationActive={false} opacity={0.5} />
              <Line type="monotone" dataKey="avg" name="avg" stroke="#2481cc" strokeWidth={2}
                dot={data.length <= 1500 ? renderFlagDot : false} isAnimationActive={false} />
              <Line type="monotone" dataKey="min" name="min" stroke="#34c759" strokeWidth={1}
                dot={false} isAnimationActive={false} opacity={0.5} />
              {data.length > 2 && (
                <Brush dataKey="label" height={22} travellerWidth={10} stroke="var(--link, #2481cc)"
                  tickFormatter={() => ""} onChange={(r: { startIndex?: number; endIndex?: number }) => setBrush(r)} />
              )}
            </LineChart>
          </ResponsiveContainer>
          <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap", marginTop: 8 }}>
            <button className="btn secondary" style={{ padding: "6px 10px", opacity: canZoomBrush ? 1 : 0.5 }}
              disabled={!canZoomBrush} onClick={zoomToBrush}>
              🔍 {t("auto.zoom_to_sel")}
            </button>
            <span style={{ fontSize: 11, color: "var(--hint)" }}>{t("auto.drill_hint")}</span>
          </div>
        </div>
      )}
    </>
  );
}

// ---------------- Anomalies ----------------

function AnomaliesTab({ qs, unit, catalog, onErr }: {
  qs: string; unit: number; catalog: AutoCatalog | null; onErr: (m: string) => void;
}) {
  const { t } = useT();
  const [data, setData] = useState<AutoAnomalies | null>(null);
  const [hideZeros, setHideZeros] = useState(false);
  const [metricFilter, setMetricFilter] = useState("");
  const [sel, setSel] = useState<AutoAnomaly | null>(null);

  useEffect(() => {
    setData(null);
    api.get<AutoAnomalies>(`/api/auto/anomalies?${qs}&sigma=2`).then(setData).catch((e: ApiError) => onErr(e.message));
  }, [qs]);

  // base = events after the hide-zeros toggle; drives both the filter and list.
  const base = useMemo(
    () => (data?.events ?? []).filter(e => !hideZeros || e.peak !== 0),
    [data, hideZeros],
  );
  // Distinct problematic metrics with a per-metric event count, worst first —
  // the options of the "pick a metric" filter.
  const metricOpts = useMemo(() => {
    const m = new Map<string, { key: string; label: string; unit: string; n: number }>();
    for (const e of base) {
      const cur = m.get(e.metric);
      if (cur) cur.n++;
      else m.set(e.metric, { key: e.metric, label: e.label, unit: e.unit, n: 1 });
    }
    return [...m.values()].sort((a, b) => b.n - a.n || a.label.localeCompare(b.label));
  }, [base]);
  // If the selected metric vanishes (e.g. hide-zeros removed all its events or
  // the timeframe changed), fall back to "all" so the list never goes blank.
  useEffect(() => {
    if (metricFilter && !metricOpts.some(o => o.key === metricFilter)) setMetricFilter("");
  }, [metricOpts, metricFilter]);

  const events = useMemo(
    () => metricFilter ? base.filter(e => e.metric === metricFilter) : base,
    [base, metricFilter],
  );

  if (!data) return <div className="empty-state"><span className="spinner" /></div>;

  return (
    <>
      <div className="card" style={{ marginBottom: 10 }}>
        <div style={{ fontSize: 12, color: "var(--hint)", marginBottom: 4 }}>{t("auto.filter_metric")}</div>
        <select value={metricFilter} style={{ width: "100%" }}
          onChange={e => { setMetricFilter(e.target.value); tg()?.HapticFeedback?.selectionChanged?.(); }}>
          <option value="">{t("auto.all_metrics")} ({base.length})</option>
          {metricOpts.map(o => (
            <option key={o.key} value={o.key}>{o.label}{o.unit ? `, ${o.unit}` : ""} ({o.n})</option>
          ))}
        </select>
      </div>

      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8, margin: "0 0 10px" }}>
        <span style={{ fontSize: 12, color: "var(--hint)" }}>
          {t("auto.anomalies_hint", { n: data.count, floor: data.rel_floor_pct })}
          {data.truncated ? ` · ${t("auto.truncated")}` : ""}
        </span>
        <label style={{ fontSize: 12, display: "flex", gap: 6, alignItems: "center", whiteSpace: "nowrap" }}>
          <input type="checkbox" checked={hideZeros} onChange={e => setHideZeros(e.target.checked)} />
          {t("auto.hide_zeros")}
        </label>
      </div>

      {events.length === 0 && <div className="empty-state">{t("auto.no_anomalies")}</div>}

      {events.map((e, i) => {
        const color = e.rel_pct >= 50 ? "#ff453a" : e.rel_pct >= 15 ? "#ff9500" : "#34c759";
        return (
          <div key={i} className="card" style={{ marginBottom: 8, borderLeft: `4px solid ${color}`, cursor: "pointer" }}
            onClick={() => { tg()?.HapticFeedback?.impactOccurred("light"); setSel(e); }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 8 }}>
              <b>{e.label}{e.unit ? <span style={{ color: "var(--hint)", fontWeight: 400 }}> {e.unit}</span> : null}</b>
              <span style={{ color, fontWeight: 700 }}>
                {e.direction === "high" ? "▲" : "▼"} {fmtNum(e.peak)} ({e.rel_pct}% {t("auto.off_normal")})
              </span>
            </div>
            <div style={{ fontSize: 12, color: "var(--hint)", marginTop: 4 }}>
              {t("auto.normal")} {fmtNum(e.mean)} · {t("auto.when")}: {fmtTs(e.start)}
              {e.end !== e.start ? ` → ${fmtTime(e.end)}` : ""} · σ {e.max_z}
            </div>
            <div style={{ fontSize: 11, color: "var(--link, #2481cc)", marginTop: 3 }}>{t("auto.tap_for_details")} ›</div>
          </div>
        );
      })}

      {sel && (
        <SnapshotModal at={sel.start} metricKey={sel.metric} unit={unit}
          catalog={catalog} onClose={() => setSel(null)} />
      )}
    </>
  );
}

function SnapshotModal({ at, metricKey, unit, catalog, onClose }: {
  at: string; metricKey: string; unit: number; catalog: AutoCatalog | null; onClose: () => void;
}) {
  const { t } = useT();
  useTelegramBackButton(onClose, [onClose]);
  const [snap, setSnap] = useState<AutoSnapshot | null>(null);
  const [err, setErr] = useState<string | null>(null);
  useEffect(() => {
    tg()?.HapticFeedback?.impactOccurred("light");
    const p = new URLSearchParams({ unit: String(unit), at });
    api.get<AutoSnapshot>(`/api/auto/snapshot?${p.toString()}`).then(setSnap).catch((e: ApiError) => setErr(e.message));
  }, [at, unit]);

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 200, display: "flex", alignItems: "flex-end" }}
      onClick={onClose}>
      <div style={{ background: "var(--bg)", width: "100%", maxHeight: "85vh", overflowY: "auto", borderRadius: "16px 16px 0 0", padding: 16 }}
        onClick={e => e.stopPropagation()}>
        <h2 style={{ marginTop: 0 }}>{t("auto.snapshot_title")}</h2>
        <div style={{ fontSize: 13, color: "var(--hint)", marginBottom: 12 }}>{fmtFull(at)}</div>
        {err && <div className="alert error">{err}</div>}
        {!snap && !err && <div className="empty-state"><span className="spinner" /></div>}
        {snap && (catalog?.groups ?? []).map(g => {
          const ms = (catalog?.metrics ?? []).filter(x => x.group === g.key && snap.metrics[x.key]);
          if (!ms.length) return null;
          return (
            <details key={g.key} className="cat-section" open>
              <summary className="cat-summary">
                <span className="cat-summary-arrow">▾</span>
                <span className="cat-summary-label">{g.label}</span>
              </summary>
              <table className="table" style={{ fontSize: 13 }}>
                <tbody>
                  {ms.map(x => {
                    const d = snap.metrics[x.key];
                    const hot = x.key === metricKey;
                    return (
                      <tr key={x.key} style={hot ? { background: "rgba(255,149,0,0.16)" } : undefined}>
                        <td>{hot ? "● " : ""}{x.label}{x.unit ? <span style={{ color: "var(--hint)" }}> {x.unit}</span> : null}</td>
                        <td className="num"><b>{fmtNum(d?.avg)}</b></td>
                        <td className="num" style={{ color: "var(--hint)" }}>{fmtNum(d?.min)}–{fmtNum(d?.max)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </details>
          );
        })}
        <button className="btn" style={{ marginTop: 12 }} onClick={onClose}>{t("common.close")}</button>
      </div>
    </div>
  );
}

// ---------------- Data (upload + imports + excel) ----------------

function DataTab({ unit, qs, onErr }: { unit: number; qs: string; onErr: (m: string) => void }) {
  const { t } = useT();
  const [imports, setImports] = useState<AutoImportRow[] | null>(null);
  const [uploading, setUploading] = useState(false);
  const [note, setNote] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement | null>(null);
  const pollRef = useRef<number | null>(null);

  const loadImports = () =>
    api.get<AutoImportRow[]>("/api/auto/imports").then(setImports).catch(() => undefined);

  useEffect(() => { loadImports(); return () => { if (pollRef.current) window.clearInterval(pollRef.current); }; }, []);

  const onPick = async (file: File) => {
    setUploading(true); setNote(null); onErr("");
    tg()?.HapticFeedback?.impactOccurred("light");
    try {
      const form = new FormData();
      form.append("file", file);
      const r = await api.upload<{ import_id: number }>(`/api/auto/import?unit=${unit}`, form);
      setNote(t("auto.upload_started"));
      if (pollRef.current) window.clearInterval(pollRef.current);
      pollRef.current = window.setInterval(async () => {
        const rows = await api.get<AutoImportRow[]>("/api/auto/imports").catch(() => null);
        if (rows) {
          setImports(rows);
          const me = rows.find(x => x.id === r.import_id);
          if (me && me.status !== "processing") {
            if (pollRef.current) window.clearInterval(pollRef.current);
            setNote(me.status === "done" ? t("auto.upload_done", { rows: me.rows }) : t("auto.upload_error"));
            tg()?.HapticFeedback?.notificationOccurred(me.status === "done" ? "success" : "error");
          }
        }
      }, 3000);
    } catch (e) {
      onErr((e as ApiError).message);
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const exportXlsx = () => {
    api.get<Blob>(`/api/auto/export?${qs}`).then(blob => {
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `auto-${Date.now()}.xlsx`; a.click();
      URL.revokeObjectURL(url);
    }).catch((e: ApiError) => onErr(e.message));
  };

  return (
    <>
      <div className="card" style={{ marginBottom: 12 }}>
        <div style={{ fontWeight: 600, marginBottom: 6 }}>{t("auto.upload_title")}</div>
        <div style={{ fontSize: 12, color: "var(--hint)", marginBottom: 10 }}>{t("auto.upload_hint")}</div>
        <input ref={fileRef} type="file" accept=".csv,.gz" disabled={uploading}
          onChange={e => { const f = e.target.files?.[0]; if (f) onPick(f); }} />
        {uploading && <div style={{ marginTop: 8 }}><span className="spinner" /> {t("auto.uploading")}</div>}
        {note && <div className="alert" style={{ marginTop: 8, background: "rgba(52,199,89,0.12)" }}>{note}</div>}
      </div>

      <div className="card" style={{ marginBottom: 12 }}>
        <button className="btn excel" onClick={exportXlsx}>{t("auto.export_xlsx")}</button>
        <div style={{ fontSize: 11, color: "var(--hint)", marginTop: 8 }}>{t("auto.export_hint")}</div>
      </div>

      <div style={{ fontSize: 12, color: "var(--hint)", margin: "0 0 6px" }}>{t("auto.imports_title")}</div>
      {!imports && <div className="empty-state"><span className="spinner" /></div>}
      {imports && imports.length === 0 && <div className="empty-state">{t("common.no_data")}</div>}
      {imports && imports.length > 0 && (
        <div className="card" style={{ overflowX: "auto" }}>
          <table className="table" style={{ fontSize: 12 }}>
            <thead><tr>
              <th>{t("auto.col_file")}</th><th>{t("auto.col_status")}</th><th>{t("auto.col_rows")}</th><th>{t("auto.col_period")}</th>
            </tr></thead>
            <tbody>
              {imports.map(im => (
                <tr key={im.id}>
                  <td style={{ maxWidth: 130, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{im.filename}</td>
                  <td>{im.status === "done" ? "✅" : im.status === "processing" ? "⏳" : "⚠️"}</td>
                  <td className="num">{im.rows || "—"}</td>
                  <td style={{ fontSize: 11 }}>{fmtTs(im.from_ts)}{im.to_ts ? ` → ${fmtTs(im.to_ts)}` : ""}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}
